import { useMutation } from '@apollo/client';
import { Banner, Button, Meta, theme } from 'aimo-ui';
import BenefitCard from 'components/account/benefits/BenefitCard';
import { showToast } from 'components/common/CustomToast';
import Icon from 'components/icons/Icon';
import AccountContext from 'context/AccountContext';
import { UNVERIFY_CUSTOMER } from 'graphql/mutations/unverifyCustomer';
import { usePermitsAndBenefits } from 'hooks/usePermitsAndBenefits';
import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { useBenefitStore } from 'stores/benefitStore';
import { StyledWrapper } from 'styles/ContainerStyles';
import {
  Mutation,
  MutationUnverifyCustomerArgs,
} from 'types/generatedSchemaTypes';
import { getBenefitsForEmail, getVerifiedEmail } from 'utils/benefitUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { useUiStore } from 'stores/uiStore';

const WarningIcon = () => <Icon name="Info-Outlined" />;

const DisconnectBenefitModal: React.FC = () => {
  const { t } = useTranslation();
  const { benefit } = useBenefitStore((state) => state);
  const { customer, refetchCustomer } = useContext(AccountContext);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const { benefits, refetchBenefits } = usePermitsAndBenefits({
    customerUid: customer.uid || undefined,
  });

  const verifiedEmail = useMemo(
    () =>
      getVerifiedEmail(
        benefit?.customerMemberMetadata?.admissionDetails,
        customer.contactEmail
      ),
    [benefit?.customerMemberMetadata?.admissionDetails, customer.contactEmail]
  );

  const connectedBenefits = useMemo(
    () => (verifiedEmail ? getBenefitsForEmail(benefits, verifiedEmail) : []),
    [benefits, verifiedEmail]
  );

  const [unverifyCustomer, { loading }] = useMutation<
    Pick<Mutation, 'unverifyCustomer'>,
    MutationUnverifyCustomerArgs
  >(UNVERIFY_CUSTOMER, {
    onCompleted: async (data) => {
      if (data?.unverifyCustomer?.status === 'SUCCESS_UNVERIFY') {
        await refetchCustomer();
        await refetchBenefits();
        useUiStore.getState().setSheetModal('');
        showToast('account.benefitDetail.disconnectSuccess', 'success');
      } else {
        showToast('account.benefitDetail.disconnectError', 'error');
      }
      setIsDisconnecting(false);
    },
    onError: () => {
      showToast('account.benefitDetail.disconnectError', 'error');
    },
  });

  const handleDisconnect = () => {
    if (!verifiedEmail) return;
    setIsDisconnecting(true);
    unverifyCustomer({
      variables: {
        input: {
          verificationSubjectId: verifiedEmail,
          externalVerificationType: 'EMAIL',
          unVerificationMethod: 'USER_INTERACTIVE',
        },
      },
    });
  };

  return (
    <View>
      <StyledWrapper mb={16}>
        <Meta color={theme.colors.gray300}>
          {t('account.benefitDetail.disconnectConfirmDescription')}
        </Meta>
      </StyledWrapper>

      <Banner
        type="warning"
        text={t('account.benefitDetail.disconnectWarning', {
          benefitName: benefit?.name,
          count: connectedBenefits.length,
          email: verifiedEmail,
        })}
        iconComponent={WarningIcon}
      />

      <StyledWrapper mb={16} mt={16}>
        {connectedBenefits.map((b, index) => (
          <BenefitCard key={b.uid ?? index} benefit={b} index={index} />
        ))}
      </StyledWrapper>

      <StyledWrapper mb={12}>
        <Button
          {...testIdentifiers('disconnect-benefit-confirm')}
          size="l"
          text={t('account.benefitDetail.disconnectConfirm')}
          onPress={handleDisconnect}
          loading={isDisconnecting}
          disabled={isDisconnecting}
          buttonType="alert"
        />
      </StyledWrapper>
      <Button
        {...testIdentifiers('disconnect-benefit-cancel')}
        size="l"
        text={t('button.cancel')}
        onPress={() => useUiStore.getState().setSheetModal('')}
        disabled={loading}
        buttonType="tertiary"
      />
    </View>
  );
};

export default DisconnectBenefitModal;
